import React, { useState, useMemo } from "react";
import { useFormik } from "formik";
import { getSubIndustryValidationSchema } from "../../validation/subIndustryValidation";
import { IoCloudUploadOutline } from "react-icons/io5";
import { RiDeleteBin5Line } from "react-icons/ri";
import { TiTick } from "react-icons/ti";
import Modal from "../../components/common/modal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../../assets/styles/tags.css";
import { useSubIndustryStore } from "../../hooks/sub-industry/sub-industry.store";
import { useSubIndustryUpdate } from "../../hooks/sub-industry/subindustry.update";
import { useViewIndustryQuery } from "../../hooks/industry/industryList";
import { toast } from "react-hot-toast";
import { useEffect } from "react";
import Toggle from "../../components/common/toggle";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const AddSubIndustry = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState();
  // "http://i.pravatar.cc/500?img=7"

  const { id } = useParams();
  const isEditMode = Boolean(id);
  const location = useLocation();
  const productToEdit = location.state?.subindustry;

  const [bulkImages, setBulkImages] = useState([]);
  const [upload, setUload] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileType, setFileType] = useState("stone");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [bulkImageFiles, setBulkImageFiles] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [deletedVideoIds, setDeletedVideoIds] = useState([]);
  const [deletedCatalogIds, setDeletedCatalogIds] = useState([]);

  const [existingCatalogs, setExistingCatalogs] = useState([]);
  const [newCatalogFiles, setNewCatalogFiles] = useState([]);

  const { mutate: addSubIndustry, isLoading: isCreating } =
    useSubIndustryStore();
  const { mutate: updateSubIndustry, isLoading: isUpdating } =
    useSubIndustryUpdate();
  const { data: industriesData } = useViewIndustryQuery();

  const industriesList = useMemo(() => {
    if (!industriesData) return [];
    const list =
      industriesData?.data?.industry ||
      industriesData?.data?.industries ||
      industriesData?.data ||
      industriesData?.industry ||
      industriesData?.industries ||
      industriesData ||
      [];
    return Array.isArray(list) ? list : [];
  }, [industriesData]);

  const hasExistingImage = useMemo(() => {
    if (!isEditMode || !productToEdit) return false;
    const thumb =
      productToEdit.image ||
      productToEdit.thumbnail_image ||
      productToEdit.thumbnail;
    return !!thumb;
  }, [isEditMode, productToEdit]);

  const initialFormValues = useMemo(() => {
    const empty = {
      title: "",
      description: "",
      slug: "",
      parentIndustry: "",
      isActive: true,
      image: null,
    };

    if (isEditMode && productToEdit) {
      const thumb =
        productToEdit.image ||
        productToEdit.thumbnail_image ||
        productToEdit.thumbnail;

      let image = null;
      if (thumb) {
        image =
          typeof thumb === "string"
            ? thumb
            : thumb.url ||
              thumb.path ||
              thumb.file ||
              thumb.image ||
              null;
      }

      return {
        ...empty,
        title: productToEdit.title || "",
        description: productToEdit.description || "",
        slug: productToEdit.slug || "",
        parentIndustry:
          productToEdit.parentIndustry?._id ||
          productToEdit.parentIndustry ||
          "",
        isActive:
          typeof productToEdit.isActive !== "undefined"
            ? productToEdit.isActive
            : productToEdit.status !== undefined
              ? productToEdit.status === true ||
                productToEdit.status === "true" ||
                productToEdit.status === 1 ||
                productToEdit.status === "active"
              : true,
        image,
      };
    }

    return empty;
  }, [isEditMode, productToEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: getSubIndustryValidationSchema(
      isEditMode,
      hasExistingImage,
    ),
    onSubmit: (values) => {
      const body = new FormData();
      body.append("title", values.title);
      body.append("description", values.description);
      if (values.slug) {
        body.append("slug", values.slug);
      }
      if (values.parentIndustry) {
        body.append("parentIndustry", values.parentIndustry);
      }
      if (isEditMode) {
        body.append("isActive", values.isActive ? "true" : "false");
        body.append("status", values.isActive ? "true" : "false");
      }

      if (thumbnailFile) {
        body.append("image", thumbnailFile);
      }

      bulkImageFiles.forEach((file, index) => {
        if (file instanceof File) {
          body.append("images", file);
        } else {
          const url = typeof file === "object" && file.url ? file.url : file;
          if (url) {
            body.append(`images[${index}][url]`, url);
          }
        }
      });

      uploadedFiles.forEach((fileObj, index) => {
        const file = fileObj.file;
        const isNewFile = file instanceof File;

        if (isNewFile) {
          body.append("catlogs", file);

          body.append(`catlogs[${index}][title]`, fileObj.title || "");
          body.append(
            `catlogs[${index}][description]`,
            fileObj.description || "",
          );
        } else if (file) {
          const existingPath =
            typeof file === "string" ? file : file.file || file.url || file.path;
          if (existingPath) {
            body.append("existingCatlogs", existingPath);

            if (fileObj.id) {
              body.append(`catlogs[${index}][_id]`, fileObj.id);
            }
            body.append(`catlogs[${index}][file]`, existingPath);
            body.append(`catlogs[${index}][title]`, fileObj.title || "");
            body.append(
              `catlogs[${index}][description]`,
              fileObj.description || "",
            );
          }
        }
      });

      deletedImageIds.forEach((deletedId) =>
        body.append("deletedImageIds[]", deletedId),
      );
      deletedCatalogIds.forEach((deletedId) =>
        body.append("deletedCatalogIds[]", deletedId),
      );

      if (isEditMode) {
        updateSubIndustry(
          { id, body },
          {
            onSuccess: () => {
              toast.success("Sub Industry updated successfully!");
              navigate("/sub-industries");
            },
            onError: (err) => {
              toast.error(
                err?.response?.data?.message ||
                  "Failed to update Sub Industry",
              );
            },
          },
        );
      } else {
        addSubIndustry(body, {
          onSuccess: () => {
            toast.success("Sub Industry created successfully!");
            navigate("/sub-industries");
          },
          onError: (err) => {
            toast.error(
              err?.response?.data?.message ||
                "Failed to create Sub Industry",
            );
          },
        });
      }
    },
  });

  const handleThumbnailChange = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      setThumbnailFile(file);
      formik.setFieldValue("image", file);
      formik.setFieldTouched("image", true);
      const reader = new FileReader();
      reader.onload = function (e) {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resolveUrl = (raw) => resolveImageUrl(raw);

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const handleBulkImageChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setBulkImageFiles((prev) => [...prev, ...files]);
      const newImages = files.map((file) => URL.createObjectURL(file));
      setBulkImages((prev) => [...prev, ...newImages]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    setBulkImageFiles((prev) => [...prev, ...imageFiles]);
    const newImages = imageFiles.map((file) => URL.createObjectURL(file));
    setBulkImages((prev) => [...prev, ...newImages]);
  };

  useEffect(() => {
    if (isEditMode && productToEdit) {
      const getPath = (raw) => {
        if (!raw) return "";
        const path =
          typeof raw === "string"
            ? raw
            : raw.url || raw.path || raw.file || raw.image || "";
        if (typeof raw === "object" && (raw._id || raw.id)) {
          return {
            _id: raw._id || raw.id,
            url: path.startsWith("http")
              ? path
              : path.startsWith("uploads")
                ? `/${path}`
                : path,
          };
        }
        if (typeof path !== "string") return "";
        if (path.startsWith("http")) return path;
        if (path.startsWith("uploads")) return path;
        if (path.startsWith("/uploads")) return path.substring(1);
        return path;
      };

      // Handle Thumbnail
      const thumb =
        productToEdit.image ||
        productToEdit.thumbnail_image ||
        productToEdit.thumbnail;
      if (thumb) {
        setImagePreview(resolveUrl(thumb));
        setThumbnailFile(getPath(thumb));
      }

      // Handle Bulk Images
      const rawImages =
        productToEdit.images;
      if (Array.isArray(rawImages)) {
        setBulkImages(rawImages.map((img) => resolveUrl(img)));
        setBulkImageFiles(rawImages.map((img) => getPath(img)));
      }

      // Handle Catalogs
      const catlogs = productToEdit.catlogs || [];
      if (Array.isArray(catlogs)) {
        const initialUploadedFiles = catlogs.map((cat, index) => {
          const fileName =
            cat.title ||
            (typeof (cat.file || cat) === "string"
              ? (cat.file || cat).split("/").pop()
              : typeof cat.url === "string"
                ? cat.url.split("/").pop()
                : `File ${index + 1}`);
          return {
            id: cat._id || cat.id || index,
            name: fileName,
            size: "N/A",
            status: "uploaded",
            title: cat.title || "",
            description: cat.description || "",
            file: cat,
          };
        });
        setUploadedFiles(initialUploadedFiles);
        setExistingCatalogs(catlogs);
      }
    }
  }, [isEditMode, productToEdit]);

  const handleDeleteImage = (indexToDelete) => {
    const imgToDel = bulkImageFiles[indexToDelete];
    if (typeof imgToDel === "object" && (imgToDel._id || imgToDel.id)) {
      setDeletedImageIds((prev) => [...prev, imgToDel._id || imgToDel.id]);
    } else if (typeof imgToDel === "string" && imgToDel.includes("uploads")) {
      // If we don't have an ID but have a path, we might need a different approach,
      // but usually existing ones have IDs in the state.
    }
    setBulkImages((prev) => prev.filter((_, index) => index !== indexToDelete));
    setBulkImageFiles((prev) =>
      prev.filter((_, index) => index !== indexToDelete),
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setSelectedImageIndex(null);
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );

    if (validFiles.length > 0) {
      setNewCatalogFiles((prev) => [...prev, ...validFiles]);
      const newFiles = validFiles.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        progress: 100,
        status: "uploaded",
        timeLeft: 0,
        title: "",
        description: "",
        file: file,
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setNewCatalogFiles((prev) => [...prev, ...files]);
      const newFiles = files.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        progress: 100,
        status: "uploaded",
        timeLeft: 0,
        title: "",
        description: "",
        file: file,
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleCatalogMetaChange = (id, field, value) => {
    setUploadedFiles((prev) =>
      prev.map((file) => (file.id === id ? { ...file, [field]: value } : file)),
    );
  };

  const handleRemoveFile = (id, index) => {
    const fileToDel = existingCatalogs[index] || newCatalogFiles[index];
    if (typeof fileToDel === "object" && (fileToDel._id || fileToDel.id)) {
      setDeletedCatalogIds((prev) => [...prev, fileToDel._id || fileToDel.id]);
    }
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
    if (index < existingCatalogs.length) {
      setExistingCatalogs((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingCatalogs.length;

      setNewCatalogFiles((prev) => prev.filter((_, i) => i !== newIndex));
    }
  };

  const handleRetryFile = (id) => {
    setUploadedFiles((prev) =>
      prev.map((file) =>
        file.id === id ? { ...file, status: "uploading", progress: 0 } : file,
      ),
    );
  };

  return (
    <>
      <div className="body">
        <h2 style={{ marginBottom: "20px" }}>
          {isEditMode ? "Edit Sub Industry" : "Add Sub Industry"}
        </h2>

        <form onSubmit={formik.handleSubmit} noValidate>
        <div className="inputFields">
          <div className="formSection">
            <div className="form-header">
              <div
                className="text-inputs"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div>
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    placeholder="Text here..."
                    style={{ width: "100%", marginTop: "10px" }}
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <p className="error-text">{formik.errors.title}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="description" className="label">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    placeholder="Type here..."
                    rows="5"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="slug">Slug</label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    placeholder="Text here..."
                    style={{ width: "100%", marginTop: "10px" }}
                    value={formik.values.slug}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.slug && formik.errors.slug && (
                    <p className="error-text">{formik.errors.slug}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="parentIndustry">Parent Industry</label>
                  <select
                    id="parentIndustry"
                    name="parentIndustry"
                    style={{ width: "100%", marginTop: "10px" }}
                    value={formik.values.parentIndustry}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">--- Select parent industry ---</option>
                    {industriesList.map((option) => (
                      <option
                        key={option._id || option.id}
                        value={option._id || option.id}
                      >
                        {option.title || option.name}
                      </option>
                    ))}
                  </select>
                  {formik.touched.parentIndustry &&
                    formik.errors.parentIndustry && (
                      <p className="error-text">
                        {formik.errors.parentIndustry}
                      </p>
                    )}
                </div>
                {isEditMode && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                    <label htmlFor="" style={{ fontWeight: "500" }}>Status</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Toggle
                        onChange={(newChecked) =>
                          formik.setFieldValue("isActive", newChecked)
                        }
                        checked={!!formik.values.isActive}
                      />
                      <span style={{ fontSize: "14px", fontWeight: "600", color: formik.values.isActive ? "#10b981" : "#ef4444" }}>
                        {formik.values.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="image-upload-section">
                <div className="avatar-upload" style={{ margin: "0" }}>
                  <div className="avatar-edit">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={(e) => handleThumbnailChange(e.target)}
                      onBlur={() => formik.setFieldTouched("image", true)}
                    />
                    <label htmlFor="imageUpload"></label>
                  </div>
                  <div className="avatar-preview">
                    <div
                      id="imagePreview"
                      style={{ backgroundImage: `url(${imagePreview})` }}
                    ></div>
                  </div>
                </div>
                {formik.touched.image && formik.errors.image && (
                  <p className="error-text">{formik.errors.image}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="formSection">
          <div className="fileUpload">
            <div className="upload-header">
              <label>
                Upload File ({existingCatalogs.length + newCatalogFiles.length})
              </label>
            </div>

            <div className="upArea">
              <div
                className="upload-area"
                onClick={() => document.getElementById("fileInput").click()}
                onDragOver={handleFileDragOver}
                onDragLeave={handleFileDragLeave}
                onDrop={handleFileDrop}
                style={{
                  border: isFileDragging
                    ? "2px dashed #0A4DA2"
                    : "2px dashed #ddd",
                  backgroundColor: isFileDragging ? "#f0f7ff" : "transparent",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              >
                <div className="plus-icon">+</div>
                <p>Click or drag files to upload</p>
                <p className="file-info">
                  It must be .pdf or .docs file(20MB Max)
                </p>
              </div>

              <input
                type="file"
                id="fileInput"
                accept=".pdf,.doc,.docx"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {uploadedFiles.length > 0 && (
                <div className="file-list">
                  {uploadedFiles.map((file, index) => (
                    <div key={file.id} className="file-item">
                      <div className="file-info-row">
                        <div className="file-icon">📄</div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-meta">
                            <span className="file-size">{file.size} mb</span>
                            {file.status === "uploading" && (
                              <>
                                <span className="file-progress">
                                  {file.progress}%
                                </span>
                                <span className="file-time">
                                  {file.timeLeft} sec left
                                </span>
                              </>
                            )}
                            {file.status === "uploaded" && (
                              <span className="file-status-text">Uploaded</span>
                            )}
                            {file.status === "failed" && (
                              <span className="file-status-text error">
                                Upload Failed
                              </span>
                            )}
                          </div>
                          <div
                            className="file-meta-inputs"
                            style={{
                              marginTop: "10px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Catalog Title"
                              value={file.title || ""}
                              onChange={(e) =>
                                handleCatalogMetaChange(
                                  file.id,
                                  "title",
                                  e.target.value,
                                )
                              }
                              
                              style={{
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <textarea
                              placeholder="Catalog Description"
                              value={file.description || ""}
                              onChange={(e) =>
                                handleCatalogMetaChange(
                                  file.id,
                                  "description",
                                  e.target.value,
                                )
                              }
                              style={{
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                minHeight: "60px",
                              }}
                            />
                          </div>
                          {file.status === "uploading" && (
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${file.progress}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="file-actions">
                        {file.status === "failed" && (
                          <button
                            className="retry-btn"
                            onClick={() => handleRetryFile(file.id)}
                          >
                            ↻
                          </button>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => handleRemoveFile(file.id, index)}
                        >
                          <RiDeleteBin5Line />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* <div className="button-group">
              <button className="cancel-btn">Cancel</button>
              <button className="save-btn" onClick={uploadImages}>
                Save
              </button>
            </div> */}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "20px",
          }}
        >
          <button
            type="button"
            className="CancelButton"
            onClick={() => navigate("/products")}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="CommonButton"
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating
              ? "Saving..."
              : isEditMode
                ? "Update Sub Industry"
                : "Create Sub Industry"}
          </button>
        </div>
        </form>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        className="image-preview-modal"
      >
        <div className="image-modal-wrapper">
          <img
            src={selectedImage}
            alt="Preview"
            className="modal-preview-image"
          />
          <button
            className="modal-delete-btn"
            onClick={() => {
              handleDeleteImage(selectedImageIndex);
              closeModal();
            }}
            title="Delete image"
          >
            <RiDeleteBin5Line />
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AddSubIndustry;
